import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Boxes, Loader2, Plus, Save, Trash2, RefreshCw, X, Pencil, RotateCcw, Upload, Copy, Info, MoveRight } from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";
import { EuroInput } from "@/components/EuroInput";
import { useRepriceAfterSave } from "@/components/offers/RepriceUnsentButton";

const inputCls = "h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium";
const labelCls = "block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold";
const eur = (v) => {
  try { return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v) || 0); }
  catch { return `${v} €`; }
};

// Iter89i (2026-02-20) — Einheitliche 6-Kategorien-Reihenfolge in Konfigurator
// und Komponenten-Kachel (User-Vorgabe Christof):
// Wärmepumpen · Speicher · Hydraulik · Elektrik · Montage · Beratung.
const CATEGORIES = [
  { key: "hardware",    label: "Wärmepumpen" },
  { key: "speicher",    label: "Speicher" },
  { key: "hydraulisch", label: "Hydraulik" },
  { key: "elektrisch",  label: "Elektrik" },
  { key: "montage",     label: "Montage" },
  { key: "beratung",    label: "Beratung" },
  { key: "addon",       label: "Sonstiges" },
];

const emptyDraft = (cat = "hardware") => ({
  category: cat, brand: "", model: "", kw: null, price_eur: 0,
  unit: cat === "montage" || cat === "beratung" ? "pauschal" : "Stk",
  specs: {}, image_url: "",
});

export default function AdminWpComponents({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const repriceAfterSave = useRepriceAfterSave();
  const [editor, setEditor] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [activeCat, setActiveCat] = useState("hardware");
  // Inline-Preis-Edit: rowId currently being edited + draft value + saving flag
  const [inlineEdit, setInlineEdit] = useState(null); // { id, value, saving }

  // Upload-Zustand fürs Produktbild (Iteration 74)
  const [uploadingImg, setUploadingImg] = useState(false);
  // Iter89p (2026-02-24) — Upload-Zustand für Produkt-Prospekt-PDF.
  const [uploadingBrochure, setUploadingBrochure] = useState(false);

  // Iter89j (2026-02-20) — Katalog-Umzug-Wizard „Hydraulik → Speicher".
  // Zweistufig: preview zeigt betroffene Zeilen, commit führt aus.
  const [migrateWizard, setMigrateWizard] = useState(null);
  // { phase: 'loading'|'preview'|'running'|'done'|'error',
  //   matches: [], skipped: 0, migrated: 0, error: null }
  const openMigrateWizard = async () => {
    setMigrateWizard({ phase: "loading" });
    try {
      const r = await api.post("/admin/wp-components/migrate-hydraulisch-to-speicher?dry_run=true");
      setMigrateWizard({ phase: "preview", ...r.data });
    } catch (e) {
      setMigrateWizard({ phase: "error", error: e.response?.data?.detail || "Preview fehlgeschlagen" });
    }
  };
  const commitMigrate = async () => {
    setMigrateWizard((s) => ({ ...(s || {}), phase: "running" }));
    try {
      const r = await api.post("/admin/wp-components/migrate-hydraulisch-to-speicher?dry_run=false");
      setMigrateWizard({ phase: "done", ...r.data });
      toast.success(`${r.data.migrated || 0} Komponenten in „Speicher" verschoben`);
      load(true);
    } catch (e) {
      setMigrateWizard({ phase: "error", error: e.response?.data?.detail || "Umzug fehlgeschlagen" });
    }
  };

  const uploadComponentImage = async (file) => {
    if (!editor || editor.isNew) {
      toast.error("Bitte zuerst die Komponente speichern, dann kannst du ein Bild hochladen.");
      return;
    }
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Nur Bilddateien erlaubt");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Datei zu groß — max. 5 MB");
      return;
    }
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/wp-components/${editor.id}/image`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setEditor({ ...editor, image_url: data.image_url });
      toast.success("Bild hochgeladen · automatisch auf 800×800 zugeschnitten");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploadingImg(false);
    }
  };

  // Iter89p (2026-02-24) — Prospekt-PDF hochladen & verknüpfen.
  const uploadComponentBrochure = async (file) => {
    if (!editor || editor.isNew) {
      toast.error("Bitte zuerst die Komponente speichern, dann kannst du einen Prospekt hochladen.");
      return;
    }
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name?.toLowerCase().endsWith(".pdf")) {
      toast.error("Nur PDF-Dateien erlaubt");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Datei zu groß — max. 25 MB");
      return;
    }
    setUploadingBrochure(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/wp-components/${editor.id}/brochure`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setEditor({
        ...editor,
        brochure_url: data.brochure_url,
        brochure_filename: data.brochure_filename,
        brochure_size_bytes: data.brochure_size_bytes,
      });
      toast.success(`Prospekt hochgeladen (${(data.brochure_size_bytes / 1024 / 1024).toFixed(1)} MB)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploadingBrochure(false);
    }
  };

  const removeComponentBrochure = async () => {
    if (!editor || editor.isNew || !editor.brochure_url) return;
    if (!window.confirm("Prospekt wirklich entfernen?")) return;
    try {
      await api.delete(`/admin/wp-components/${editor.id}/brochure`);
      setEditor({
        ...editor,
        brochure_url: null,
        brochure_filename: null,
        brochure_size_bytes: null,
      });
      toast.success("Prospekt entfernt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
    }
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/admin/wp-components?include_inactive=${showInactive}`);
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Daten konnten nicht geladen werden");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showInactive]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const [sortKey, setSortKey] = useState("brand");
  const [sortDir, setSortDir] = useState("asc");

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    const base = rows.filter(r => r.category === activeCat);
    // Iter88n (2026-02-19): Sortierbare Marke/Modell/kW-Spalten für schnelles
    // Duplikat-Aufspüren. Sortiert lokal, damit Server-Traffic gering bleibt.
    const dir = sortDir === "desc" ? -1 : 1;
    const cmp = (a, b) => {
      const av = a?.[sortKey];
      const bv = b?.[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), "de", { sensitivity: "base", numeric: true }) * dir;
    };
    return [...base].sort(cmp);
  }, [rows, activeCat, sortKey, sortDir]);
  const counts = useMemo(() => {
    const m = {};
    for (const r of rows) m[r.category] = (m[r.category] || 0) + 1;
    return m;
  }, [rows]);

  const openNew = () => setEditor({ isNew: true, ...emptyDraft(activeCat) });
  const openEdit = (r) => setEditor({
    isNew: false, id: r.id,
    category: r.category, brand: r.brand || "", model: r.model || "",
    kw: r.kw ?? null, price_eur: r.price_eur || 0, unit: r.unit || "Stk",
    specs: r.specs || {}, image_url: r.image_url || "",
    brochure_url: r.brochure_url || null,
    brochure_filename: r.brochure_filename || null,
    brochure_size_bytes: r.brochure_size_bytes || null,
  });

  const save = async () => {
    if (!editor) return;
    if (!editor.brand.trim() || !editor.model.trim()) {
      toast.error("Marke und Modell sind Pflichtfelder"); return;
    }
    // Iter88zc — Beschreibung ist ab jetzt Pflicht damit im Angebots-PDF
    // pro Position ein Fachtext ausgewiesen werden kann (30+ Zeichen).
    const _desc = String(editor.specs?.description || "").trim();
    if (_desc.length < 30) {
      toast.error("Beschreibung ist Pflicht (mindestens 30 Zeichen) — erscheint im Angebots-PDF");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        category: editor.category, brand: editor.brand.trim(), model: editor.model.trim(),
        kw: editor.kw != null && editor.kw !== "" ? Number(editor.kw) : null,
        price_eur: Number(editor.price_eur) || 0, unit: editor.unit || "Stk",
        specs: editor.specs || {},
        image_url: (editor.image_url || "").trim() || null,
      };
      if (editor.isNew) {
        await api.post("/admin/wp-components", payload);
        toast.success("Komponente angelegt");
      } else {
        await api.patch(`/admin/wp-components/${editor.id}`, payload);
        toast.success("Komponente aktualisiert");
      }
      const savedId = editor.isNew ? null : editor.id;
      setEditor(null);
      await load();
      if (savedId) repriceAfterSave.check({ component_id: savedId }, `${payload.brand} ${payload.model}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Komponente "${r.brand} ${r.model}" deaktivieren?`)) return;
    try {
      await api.delete(`/admin/wp-components/${r.id}`);
      toast.success("Deaktiviert");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
  };

  const reactivate = async (r) => {
    try {
      await api.post(`/admin/wp-components/${r.id}/reactivate`);
      toast.success("Reaktiviert");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
  };

  const duplicateComponent = async (r) => {
    // Iteration 78: interaktiver Modell-Prompt — Nutzer:in kann direkt den
    // finalen Modellnamen tippen, ohne den Editor extra öffnen zu müssen.
    const suggested = `${r.model || "Modell"} (Kopie)`;
    const chosen = window.prompt("Modellname der Kopie:", suggested);
    if (chosen === null) return; // Abbrechen
    const finalName = chosen.trim() || suggested;
    try {
      const { data: copy } = await api.post(`/admin/wp-components/${r.id}/duplicate`);
      if (finalName !== copy.model) {
        try {
          await api.patch(`/admin/wp-components/${copy.id}`, {
            category: copy.category,
            brand: copy.brand,
            model: finalName,
            kw: copy.kw ?? null,
            price_eur: copy.price_eur || 0,
            unit: copy.unit || "Stk",
            specs: copy.specs || {},
            image_url: copy.image_url || null,
          });
          copy.model = finalName;
        } catch { /* Kopie bleibt mit Default-Namen bestehen */ }
      }
      toast.success(`Kopiert: „${copy.brand} ${finalName}"`);
      await load();
      openEdit({ ...copy, model: finalName });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kopieren fehlgeschlagen");
    }
  };

  // Inline-Preis-Update: startet Edit-Modus mit aktuellem Wert
  const startInlineEdit = (r) => {
    setInlineEdit({ id: r.id, value: String(r.price_eur ?? 0), saving: false });
  };
  const cancelInlineEdit = () => setInlineEdit(null);
  // Speichert den neuen Netto-Preis via PATCH und aktualisiert die Zeile lokal
  // (ohne kompletten Reload — die WP-Pakete rechnen ohnehin live aus wp_components).
  const commitInlineEdit = async () => {
    if (!inlineEdit) return;
    const raw = String(inlineEdit.value).replace(",", ".").trim();
    const num = Number(raw);
    if (!Number.isFinite(num) || num < 0) {
      toast.error("Bitte einen gültigen Preis eingeben (z. B. 5993,95)");
      return;
    }
    const row = rows.find(r => r.id === inlineEdit.id);
    if (!row) { setInlineEdit(null); return; }
    setInlineEdit({ ...inlineEdit, saving: true });
    try {
      // Backend PATCH erwartet die volle Komponenten-Struktur — wir schicken
      // die bestehenden Felder mit und ändern nur `price_eur`.
      await api.patch(`/admin/wp-components/${inlineEdit.id}`, {
        category: row.category,
        brand: row.brand,
        model: row.model,
        kw: row.kw ?? null,
        price_eur: num,
        unit: row.unit || "Stk",
        specs: row.specs || {},
      });
      setRows(prev => prev.map(r => r.id === inlineEdit.id
        ? { ...r, price_eur: num, price_eur_gross: Math.round(num * 1.19 * 100) / 100 }
        : r));
      toast.success(`Preis aktualisiert: ${eur(num)} netto`);
      setInlineEdit(null);
      repriceAfterSave.check({ component_id: row.id }, `${row.brand} ${row.model}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
      setInlineEdit({ ...inlineEdit, saving: false });
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-wp-components">
      {!embedded && <AppHeader title="WP-Komponenten" back="/dashboard" />}
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Boxes className="text-[#00E575]" size={22} />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold">WP-Komponenten</h1>
              <p className="text-[12px] text-zinc-300 font-medium">Einzel-Komponenten für WP-Pakete · Preis-Spalte anklicken zum direkten Bearbeiten · WP-Pakete rechnen automatisch neu</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[12px] text-zinc-400 inline-flex items-center gap-2 cursor-pointer font-medium">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
                data-testid="wp-comp-toggle-inactive" className="accent-[#00E575]" />
              Inaktive
            </label>
            <button onClick={load} className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/40 text-[12px] inline-flex items-center gap-1.5 font-medium">
              <RefreshCw size={13} /> Aktualisieren
            </button>
            <button onClick={openMigrateWizard} data-testid="wp-comp-migrate-wizard"
              title='Alle Puffer-/Warmwasser-/Solarthermie-Komponenten aus „Hydraulik" in die neue Kategorie „Speicher" verschieben.'
              className="h-9 px-3 rounded-lg bg-sky-500/20 border border-sky-500/40 hover:bg-sky-500/30 text-sky-100 text-[12px] font-bold inline-flex items-center gap-1.5">
              <MoveRight size={13} /> Umzug „Speicher"
            </button>
            {repriceAfterSave.dialog}
            <button onClick={openNew} data-testid="wp-comp-new"
              className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964]">
              <Plus size={14} /> Neu
            </button>
          </div>
        </div>

        <div className="flex gap-1 flex-wrap">
          {CATEGORIES.map(c => (
            <button key={c.key} onClick={() => setActiveCat(c.key)}
              data-testid={`wp-comp-tab-${c.key}`}
              className={`h-9 px-3 text-[11.5px] uppercase tracking-widest rounded-lg font-bold ${activeCat === c.key ? "bg-[#00E575] text-[#09090B]" : "bg-[#18181B] border border-zinc-700 text-zinc-300"}`}>
              {c.label} <span className="opacity-60 ml-1">{counts[c.key] || 0}</span>
            </button>
          ))}
        </div>

        {loading && <div className="p-8 flex justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>}

        {!loading && filtered.length === 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm">
            Keine Komponenten in dieser Kategorie.
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#0F0F11] text-zinc-400 border-b border-zinc-800">
                <tr>
                  <SortableWpTh label="Marke" col="brand" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="left" />
                  <SortableWpTh label="Modell / Bezeichnung" col="model" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="left" />
                  <SortableWpTh label="kW" col="kw" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                  <th className="text-left px-3 py-2 uppercase tracking-widest text-[12px] font-bold">Einheit</th>
                  <SortableWpTh label="Preis netto" col="price_eur" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                  <th className="text-right px-3 py-2 uppercase tracking-widest text-[12px] font-bold">Preis brutto (19%)</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isEditing = inlineEdit && inlineEdit.id === r.id;
                  return (
                  <tr key={r.id} data-testid={`wp-comp-row-${r.id}`}
                      className={`border-b border-zinc-900 hover:bg-[#0F0F11] ${!r.active ? "opacity-60" : ""}`}>
                    <td className="px-3 py-2 font-bold">{r.brand}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span>{r.model}</span>
                        {!(r.specs?.description || "").trim() && (
                          <span
                            data-testid={`wp-comp-warn-nodesc-${r.id}`}
                            title="Keine Beschreibung — im Angebots-PDF fehlt der Fachtext"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-rose-900/30 border border-rose-500/40 text-rose-300 text-[10px] font-bold uppercase tracking-wider">
                            ⚠ Keine Beschr.
                          </span>
                        )}
                        {r.category === "hardware" && !r.brochure_url && (
                          <span
                            data-testid={`wp-comp-warn-nodatasheet-${r.id}`}
                            title="Kein Datenblatt hinterlegt — Angebots-Mails gehen ohne Datenblatt raus"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-900/30 border border-amber-500/40 text-amber-300 text-[10px] font-bold uppercase tracking-wider">
                            ⚠ Kein Datenblatt
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">{r.kw ?? "—"}</td>
                    <td className="px-3 py-2 text-zinc-400">{r.unit || "Stk"}</td>
                    <td className="px-3 py-2 text-right">
                      {isEditing ? (
                        <div className="inline-flex items-center gap-1 justify-end">
                          <input
                            autoFocus
                            type="text"
                            inputMode="decimal"
                            value={inlineEdit.value}
                            onChange={(e) => setInlineEdit({ ...inlineEdit, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); commitInlineEdit(); }
                              if (e.key === "Escape") { e.preventDefault(); cancelInlineEdit(); }
                            }}
                            disabled={inlineEdit.saving}
                            data-testid={`wp-comp-inline-price-input-${r.id}`}
                            className="h-8 w-28 bg-[#09090B] border border-[#00E575] rounded px-2 text-right text-[12px] text-zinc-50 outline-none disabled:opacity-50 font-medium"
                            placeholder="0,00"
                          />
                          <button onClick={commitInlineEdit} disabled={inlineEdit.saving}
                            data-testid={`wp-comp-inline-price-save-${r.id}`}
                            title="Speichern (Enter)"
                            className="h-7 w-7 rounded bg-[#00E575] text-[#09090B] hover:bg-[#00C964] inline-flex items-center justify-center disabled:opacity-50">
                            {inlineEdit.saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                          </button>
                          <button onClick={cancelInlineEdit} disabled={inlineEdit.saving}
                            data-testid={`wp-comp-inline-price-cancel-${r.id}`}
                            title="Abbrechen (Esc)"
                            className="h-7 w-7 rounded bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center">
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => startInlineEdit(r)}
                          data-testid={`wp-comp-inline-price-${r.id}`}
                          title="Klicken zum Bearbeiten"
                          className="group text-right hover:bg-zinc-800/60 rounded px-2 py-0.5 -mx-2 inline-flex items-center gap-1 text-zinc-100">
                          {eur(r.price_eur)}
                          <Pencil size={9} className="text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-[#00E575] font-bold">{eur(r.price_eur_gross)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(r)} data-testid={`wp-comp-edit-${r.id}`}
                        title="Bearbeiten"
                        className="h-7 w-7 rounded bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center mr-1">
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => duplicateComponent(r)} data-testid={`wp-comp-duplicate-${r.id}`}
                        title="Kopieren (Duplikat als neue Komponente)"
                        className="h-7 w-7 rounded bg-sky-500/10 border border-sky-500/40 text-sky-300 hover:bg-sky-500/20 inline-flex items-center justify-center mr-1">
                        <Copy size={12} />
                      </button>
                      {r.active !== false ? (
                        <button onClick={() => remove(r)} data-testid={`wp-comp-delete-${r.id}`}
                          className="h-7 w-7 rounded bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 inline-flex items-center justify-center">
                          <Trash2 size={12} />
                        </button>
                      ) : (
                        <button onClick={() => reactivate(r)} data-testid={`wp-comp-reactivate-${r.id}`}
                          className="h-7 w-7 rounded bg-amber-500/10 border border-amber-500/40 text-amber-400 hover:bg-amber-500/20 inline-flex items-center justify-center">
                          <RotateCcw size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {editor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
             data-testid="wp-comp-editor">
          <div className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-2xl w-full">
            <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-4">
              <h2 className="font-display text-lg font-bold">
                {editor.isNew ? "Neue Komponente" : `Komponente bearbeiten`}
              </h2>
              <button onClick={() => setEditor(null)} className="h-8 w-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center">
                <X size={14} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Kategorie</label>
                  <select value={editor.category} onChange={(e) => setEditor({ ...editor, category: e.target.value })}
                    data-testid="wp-comp-editor-cat" className={inputCls}>
                    {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                  {/* Iter89j (2026-02-20) — Speicher-Reminder. Wenn Kategorie
                      auf `hydraulisch` steht und der Modellname stark nach
                      Puffer/Speicher/Warmwasser aussieht, blende einen
                      1-Klick-Umzugs-Hinweis ein. */}
                  {editor.category === "hydraulisch" && /(?:speicher|puffer|warmwasser|ww-|ww_|hydraulikweiche|solarther)/i.test(editor.model || "") && (
                    <div className="mt-2 rounded-lg border border-sky-500/50 bg-sky-500/10 px-3 py-2 flex items-start gap-2" data-testid="speicher-hint">
                      <Info size={13} className="text-sky-300 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-bold text-sky-200">Vielleicht doch „Speicher"?</div>
                        <div className="text-[11px] text-sky-100/80 leading-snug mt-0.5">
                          Der Modellname enthält „{(editor.model || "").match(/(?:speicher|puffer|warmwasser|ww-|ww_|hydraulikweiche|solarther)/i)?.[0]}" — für einheitliche Sortierung im Konfigurator und PDF wäre die Kategorie <b>Speicher</b> passender.
                        </div>
                        <button
                          type="button"
                          onClick={() => setEditor({ ...editor, category: "speicher" })}
                          data-testid="speicher-hint-apply"
                          className="mt-1.5 h-7 px-2.5 rounded-md bg-sky-500/25 hover:bg-sky-500/40 text-sky-100 text-[11.5px] font-bold border border-sky-500/50"
                        >
                          → Kategorie auf „Speicher" ändern
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Einheit</label>
                  <select value={editor.unit} onChange={(e) => setEditor({ ...editor, unit: e.target.value })}
                    className={inputCls}>
                    <option value="Stk">Stk</option>
                    <option value="pauschal">pauschal</option>
                    <option value="m">m</option>
                    <option value="m²">m²</option>
                    <option value="h">h</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Marke</label>
                  <input value={editor.brand} onChange={(e) => setEditor({ ...editor, brand: e.target.value })}
                    data-testid="wp-comp-editor-brand" className={inputCls} placeholder="HEIKO / LG / Novatus / …" />
                </div>
                <div>
                  <label className={labelCls}>kW (optional)</label>
                  <input type="number" step="0.1" value={editor.kw ?? ""}
                    onChange={(e) => setEditor({ ...editor, kw: e.target.value === "" ? null : Number(e.target.value) })}
                    className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Modell / Bezeichnung</label>
                <input value={editor.model} onChange={(e) => setEditor({ ...editor, model: e.target.value })}
                  data-testid="wp-comp-editor-model" className={inputCls} placeholder="z. B. Thermal Plus 9 kW / 200-Liter Pufferspeicher" />
              </div>
              <div>
                <label className={labelCls}>Preis netto (€)</label>
                <EuroInput value={editor.price_eur}
                  onChange={(v) => setEditor({ ...editor, price_eur: v })}
                  testid="wp-comp-editor-price" className={inputCls} />
                <p className="text-[12px] text-zinc-300 mt-1">Brutto (19% MwSt.): {eur((Number(editor.price_eur) || 0) * 1.19)}</p>
              </div>
              <div>
                <label className={labelCls}>
                  Beschreibung <span className="text-rose-300">*</span>
                  <span className="text-zinc-500 font-normal"> — erscheint im Angebots-PDF (min. 30 Zeichen)</span>
                </label>
                <textarea
                  value={editor.specs?.description || ""}
                  onChange={(e) => setEditor({ ...editor, specs: { ...(editor.specs || {}), description: e.target.value } })}
                  data-testid="wp-comp-editor-description"
                  className={`${inputCls} h-20 py-2 resize-none ${
                    String(editor.specs?.description || "").trim().length < 30 ? "border-rose-500/40" : ""
                  }`}
                  placeholder="z. B. Fachgerechter Potentialausgleich der Wärmepumpen-Außeneinheit nach DIN VDE 0100-540…"
                />
                <p className={`text-[11px] mt-1 ${
                  String(editor.specs?.description || "").trim().length < 30 ? "text-rose-300" : "text-zinc-500"
                }`}>
                  {String(editor.specs?.description || "").trim().length} / 30 Zeichen
                </p>
              </div>
              {/* Iter88zz9-b — Symmetrisch zum PV-Katalog: optionale
                  Eigenschaften und Kundenvorteile. Erscheinen im Angebots-PDF
                  im Detail-Bereich pro Komponente. */}
              <div>
                <label className={labelCls}>
                  Eigenschaften <span className="text-zinc-500 font-normal">— optional, technische Merkmale</span>
                </label>
                <textarea
                  value={editor.specs?.properties || ""}
                  onChange={(e) => setEditor({ ...editor, specs: { ...(editor.specs || {}), properties: e.target.value } })}
                  data-testid="wp-comp-editor-properties"
                  className={`${inputCls} h-14 py-2 resize-none`}
                  placeholder="z. B. R290-Kältemittel · SCOP 4,7 · Schall 55 dB(A) · WLAN-Modul integriert"
                />
              </div>
              <div>
                <label className={labelCls}>
                  Kundenvorteile <span className="text-zinc-500 font-normal">— optional, Nutzen für den Endkunden</span>
                </label>
                <textarea
                  value={editor.specs?.advantages || ""}
                  onChange={(e) => setEditor({ ...editor, specs: { ...(editor.specs || {}), advantages: e.target.value } })}
                  data-testid="wp-comp-editor-advantages"
                  className={`${inputCls} h-14 py-2 resize-none`}
                  placeholder="z. B. Niedriger Stromverbrauch · leiser Betrieb · zukunftssicher (Kältemittel-Verordnung)"
                />
              </div>
              <div>
                <label className={labelCls}>Produktbild — Datei oder URL</label>
                <div className="flex gap-2">
                  <input
                    value={editor.image_url || ""}
                    onChange={(e) => setEditor({ ...editor, image_url: e.target.value })}
                    data-testid="wp-comp-editor-image-url"
                    className={`${inputCls} flex-1`}
                    placeholder="https://…/wp-produktbild.jpg oder Bild hochladen ↑"
                  />
                  <label
                    className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-lg font-bold text-[12px] cursor-pointer transition ${
                      uploadingImg
                        ? "bg-zinc-700 text-zinc-400 cursor-wait"
                        : editor.isNew
                          ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                          : "bg-[#00E575] text-[#09090B] hover:bg-[#00C964]"
                    }`}
                    title={editor.isNew ? "Erst Komponente speichern, dann Bild hochladen" : "Bild von Rechner hochladen (wird auf 800×800 zugeschnitten)"}
                    data-testid="wp-comp-editor-image-upload-label"
                  >
                    {uploadingImg ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="wp-comp-editor-image-file"
                      disabled={editor.isNew || uploadingImg}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadComponentImage(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
                <p className="text-[12px] text-zinc-400 mt-1">
                  Direkt-Upload wird automatisch auf 800×800 px zugeschnitten (weißer Rand, JPEG). Alternativ eine öffentliche HTTPS-URL eintragen — beides landet im nächsten Angebots-PDF neben der Komponente.
                </p>
                {editor.image_url ? (
                  <div className="mt-2 rounded-lg overflow-hidden bg-white inline-block border border-zinc-700">
                    <img
                      src={editor.image_url}
                      alt="Vorschau Produktbild"
                      data-testid="wp-comp-editor-image-preview"
                      className="h-24 w-24 object-contain"
                      onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                    />
                  </div>
                ) : null}
              </div>

              {/* Iter89p (2026-02-24) — Prospekt-PDF-Zuordnung pro Produkt.
                  Admin lädt EINE PDF hoch, die beim Versand des initialen
                  Angebots automatisch mit angehängt wird. Fällt bei >15 MB
                  Mail-Gesamtgröße auf Download-Link zurück. */}
              <div>
                <label className={labelCls}>Prospekt-PDF (optional) — wird dem Angebotsversand automatisch beigelegt</label>
                <div className="flex items-center gap-2">
                  <label
                    className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-lg font-bold text-[12px] cursor-pointer transition ${
                      uploadingBrochure
                        ? "bg-zinc-700 text-zinc-400 cursor-wait"
                        : editor.isNew
                          ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                          : "bg-[#00E575] text-[#09090B] hover:bg-[#00C964]"
                    }`}
                    title={editor.isNew ? "Erst Komponente speichern, dann Prospekt hochladen" : "Prospekt-PDF hochladen (max. 25 MB)"}
                    data-testid="wp-comp-editor-brochure-upload-label"
                  >
                    {uploadingBrochure ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                    {editor.brochure_url ? "Prospekt austauschen" : "Prospekt hochladen"}
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      data-testid="wp-comp-editor-brochure-file"
                      disabled={editor.isNew || uploadingBrochure}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadComponentBrochure(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {editor.brochure_url && (
                    <>
                      <a
                        href={editor.brochure_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="wp-comp-editor-brochure-preview-link"
                        className="h-10 px-3 inline-flex items-center gap-1.5 rounded-lg bg-[#18181B] border border-zinc-700 text-[12px] font-medium text-zinc-200 hover:border-[#00E575]"
                        title="Prospekt in neuem Tab öffnen"
                      >
                        Vorschau
                      </a>
                      <button
                        type="button"
                        onClick={removeComponentBrochure}
                        data-testid="wp-comp-editor-brochure-remove"
                        className="h-10 px-3 inline-flex items-center gap-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-[12px] font-medium text-red-400 hover:bg-red-900/30"
                      >
                        Entfernen
                      </button>
                    </>
                  )}
                </div>
                {editor.brochure_url ? (
                  <p className="text-[11.5px] text-zinc-300 mt-1.5" data-testid="wp-comp-editor-brochure-info">
                    <b>{editor.brochure_filename || "Prospekt.pdf"}</b>
                    {editor.brochure_size_bytes ? ` · ${(editor.brochure_size_bytes / 1024 / 1024).toFixed(1)} MB` : ""}
                    {" "}· wird beim <b>initialen Angebotsversand</b> automatisch an die Kunden-Mail angehängt und ist im Projekt-Konfigurator über „Datenblatt zeigen“ für alle Nutzer sichtbar.
                  </p>
                ) : (
                  <p className="text-[12px] text-zinc-400 mt-1">
                    Optional. Wenn hochgeladen, hängt das System die Datei beim Versand des initialen Angebots automatisch an die Kunden-Mail. Bei {">"}15 MB Mail-Gesamtgröße wird stattdessen ein Download-Link eingebettet.
                  </p>
                )}
              </div>
            </div>
            <div className="border-t border-zinc-800 px-5 py-3 bg-[#0F0F11] flex justify-end gap-2">
              <button onClick={() => setEditor(null)} className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-700 text-[12px] font-medium">
                Abbrechen
              </button>
              <button onClick={save} disabled={saving}
                data-testid="wp-comp-editor-save"
                className="h-9 px-4 rounded-lg bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter89j (2026-02-20) — Umzug-Wizard „Hydraulik → Speicher". */}
      {migrateWizard && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-3" data-testid="wp-comp-migrate-wizard-modal">
          <div className="bg-[#18181B] border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="border-b border-zinc-800 px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-[12px] uppercase tracking-widest text-sky-300">Katalog-Umzug</div>
                <div className="font-bold text-[15px] text-zinc-100">Hydraulik → Speicher</div>
              </div>
              <button onClick={() => setMigrateWizard(null)} className="h-8 px-3 text-[12px] bg-zinc-800 rounded font-bold">
                <X size={14} className="inline"/> Schließen
              </button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              {migrateWizard.phase === "loading" && (
                <div className="text-center py-6 text-zinc-300 inline-flex items-center gap-2 justify-center w-full">
                  <Loader2 size={16} className="animate-spin"/> Suche Kandidaten…
                </div>
              )}
              {migrateWizard.phase === "error" && (
                <div className="rounded-lg bg-red-500/10 border border-red-500/40 text-red-200 text-[13px] p-3">
                  <b>Fehler:</b> {migrateWizard.error}
                </div>
              )}
              {(migrateWizard.phase === "preview" || migrateWizard.phase === "running" || migrateWizard.phase === "done") && (
                <>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-[#0F0F11] border border-zinc-800 rounded-lg py-2">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-400">Hydraulik total</div>
                      <div className="font-display text-xl font-bold">{migrateWizard.total_hydraulisch ?? 0}</div>
                    </div>
                    <div className="bg-sky-500/10 border border-sky-500/40 rounded-lg py-2">
                      <div className="text-[10px] uppercase tracking-widest text-sky-300">Wird umgezogen</div>
                      <div className="font-display text-xl font-bold text-sky-100" data-testid="wizard-will-migrate">{migrateWizard.will_migrate ?? migrateWizard.migrated ?? 0}</div>
                    </div>
                    <div className="bg-[#0F0F11] border border-zinc-800 rounded-lg py-2">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-400">Bleibt Hydraulik</div>
                      <div className="font-display text-xl font-bold text-zinc-100">{migrateWizard.skipped ?? 0}</div>
                    </div>
                  </div>
                  {migrateWizard.phase === "done" ? (
                    <div className="rounded-lg bg-[#00E575]/10 border border-[#00E575]/40 text-[#00E575] text-[13px] p-3" data-testid="wizard-done">
                      ✓ {migrateWizard.migrated} Komponenten wurden erfolgreich in die Kategorie „Speicher" verschoben.
                    </div>
                  ) : (
                    <>
                      <div>
                        <div className="text-[11.5px] font-bold text-zinc-300 uppercase tracking-wider mb-1">Umzugs-Kandidaten (Vorschau):</div>
                        <div className="max-h-64 overflow-y-auto border border-zinc-800 rounded-lg divide-y divide-zinc-800">
                          {(migrateWizard.matches || []).length === 0 && (
                            <div className="p-3 text-[12.5px] text-zinc-400 text-center">Keine passenden Komponenten in „Hydraulik" gefunden.</div>
                          )}
                          {(migrateWizard.matches || []).map((m) => (
                            <div key={m.id} className="p-2.5 flex items-center justify-between gap-3 text-[12.5px]" data-testid={`wizard-match-${m.id}`}>
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-zinc-100 truncate">{m.brand} · {m.model}</div>
                                {m.kw != null && <div className="text-[11px] text-zinc-500 font-mono">{m.kw} kW · {Number(m.price_eur || 0).toLocaleString("de-DE")} €</div>}
                              </div>
                              <MoveRight size={13} className="text-sky-400 flex-shrink-0"/>
                              <div className="text-[11px] font-bold text-sky-300">Speicher</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <button onClick={() => setMigrateWizard(null)}
                          className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12px] font-bold">
                          Abbrechen
                        </button>
                        <button onClick={commitMigrate}
                          disabled={migrateWizard.phase === "running" || (migrateWizard.will_migrate ?? 0) === 0}
                          data-testid="wizard-commit"
                          className="h-9 px-4 rounded-lg bg-sky-500 hover:bg-sky-400 text-white text-[12px] font-bold disabled:opacity-50 inline-flex items-center gap-1.5">
                          {migrateWizard.phase === "running" ? <Loader2 size={13} className="animate-spin"/> : <MoveRight size={13}/>}
                          {migrateWizard.will_migrate} Komponente{(migrateWizard.will_migrate || 0) === 1 ? "" : "n"} verschieben
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Iter88n — Sortier-Header analog `SortableTh` in `AdminPvCatalog.jsx`.
function SortableWpTh({ label, col, sortKey, sortDir, onClick, align = "left" }) {
  const active = sortKey === col;
  const alignClass = align === "right" ? "text-right" : "text-left";
  return (
    <th
      onClick={() => onClick(col)}
      className={`${alignClass} px-3 py-2 uppercase tracking-widest text-[12px] font-bold cursor-pointer select-none hover:text-emerald-300 transition ${active ? "text-emerald-300" : ""}`}
      data-testid={`wp-sort-th-${col}`}
      title={`Nach ${label} sortieren${active ? " (umschalten)" : ""}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[10px] ${active ? "opacity-100" : "opacity-30"}`}>
          {active ? (sortDir === "desc" ? "▼" : "▲") : "↕"}
        </span>
      </span>
    </th>
  );
}

